/**
 * Catalog of implemented reward distribution methods (动静态体系).
 * Admin UI: GET/PUT `/api/admin/reward-distributions`.
 * Runtime overrides via `uo_config` keys; fallback to code defaults in this file.
 */
import {
  BALANCE_AWARD_PCT,
  DYNAMIC_REWARD_CONFIG_KEYS,
  PEER_LEVEL_BONUS_EP,
  SHARE_BONUS_E1_CAPS,
  SHARE_BONUS_E2_CAPS,
  SHARE_BONUS_E3_TOTAL,
} from './dynamic-reward.js';
import { REGISTRATION_CONFIG_KEYS, REGISTRATION_PACKAGE_DEFAULTS } from './registration-package.js';
import {
  MAIN_EXIT_MULTIPLIER,
  MAIN_STAR_PRINCIPAL_USD,
  STATIC_PAYOUT_SPLIT,
  SUB_REINVEST_EXIT_MULTIPLIER,
  WITHDRAW_SPLIT_DYNAMIC,
  WITHDRAW_SPLIT_STATIC,
} from './static-reward.js';

export type RewardDistributionCategory =
  | 'static'
  | 'dynamic'
  | 'withdraw_split'
  | 'activation'
  | 'quota'
  | 'feature';

export type RewardDistributionValueKind = 'percent' | 'amount' | 'json' | 'boolean';

export interface RewardDistributionDef {
  id: string;
  category: RewardDistributionCategory;
  title_zh: string;
  description_zh: string;
  /** When / how payout is triggered */
  trigger_zh: string;
  theory_ref?: string;
  config_key: string | null;
  value_kind: RewardDistributionValueKind;
  /** Serialized default (JSON string for `json` kind) */
  default_value: string;
  editable: boolean;
  ledger_actions?: string[];
}

export const REWARD_DISTRIBUTION_REGISTRY: RewardDistributionDef[] = [
  {
    id: 'static_payout_split',
    category: 'static',
    title_zh: '静态奖发放比例',
    description_zh: '达标事件金额按资产桶拆分入账（EP1/SP1/RP/FC）',
    trigger_zh: '晋级/子号安置/主号开通等触发 tryPayOnTarget',
    theory_ref: '§3',
    config_key: 'reward.static.payout_split',
    value_kind: 'json',
    default_value: JSON.stringify(
      STATIC_PAYOUT_SPLIT.map((r) => ({ asset: r.asset, pct: r.pct, leg: 'credit' })),
      null,
      2,
    ),
    editable: true,
    ledger_actions: ['static_reward_ep1', 'static_reward_sp1', 'static_reward_rp', 'static_reward_fc'],
  },
  {
    id: 'static_main_principal',
    category: 'static',
    title_zh: '主号本金快照（USD）',
    description_zh: 'E1/E2/E3 套餐对应 principal_snapshot，开通时写入用户',
    trigger_zh: '主号开通 / 子号 link 时快照',
    theory_ref: '§3',
    config_key: 'reward.static.main_principal_usd',
    value_kind: 'json',
    default_value: JSON.stringify(MAIN_STAR_PRINCIPAL_USD, null, 2),
    editable: true,
  },
  {
    id: 'static_main_exit_multiplier',
    category: 'static',
    title_zh: '主号出局倍数',
    description_zh: '按 level_code（E1/E2/E3）× 本金 = 静态封顶',
    trigger_zh: '快照时读取；已快照用户不 retro 改',
    theory_ref: '§3',
    config_key: 'reward.static.main_exit_multiplier',
    value_kind: 'json',
    default_value: JSON.stringify(MAIN_EXIT_MULTIPLIER, null, 2),
    editable: true,
  },
  {
    id: 'static_sub_exit_multiplier',
    category: 'static',
    title_zh: '子号出局倍数',
    description_zh: '复投币 sp1/sp2/sp3 对应倍数',
    trigger_zh: '子号 admin link 时快照',
    theory_ref: '§3',
    config_key: 'reward.static.sub_exit_multiplier',
    value_kind: 'json',
    default_value: JSON.stringify(SUB_REINVEST_EXIT_MULTIPLIER, null, 2),
    editable: true,
  },
  {
    id: 'dynamic_binary_pairing',
    category: 'dynamic',
    title_zh: '双轨对碰',
    description_zh: '弱区奖励值（配套100/1000）为对碰额；例左1k右2.5k只计1k；强区减对碰额后结转',
    trigger_zh: '业绩事件（激活/子号/充值审核 perf feed）',
    theory_ref: '§5.1',
    config_key: null,
    value_kind: 'json',
    default_value: JSON.stringify({ algorithm: 'min(left,right)', editable: false }),
    editable: false,
    ledger_actions: ['dynamic_pairing'],
  },
  {
    id: 'dynamic_balance_award',
    category: 'dynamic',
    title_zh: '平衡奖',
    description_zh: '对碰额 × 比例 → EP（受周封顶）',
    trigger_zh: '对碰后自动；可 admin 手动补跑',
    theory_ref: '§5.2',
    config_key: DYNAMIC_REWARD_CONFIG_KEYS.BALANCE_AWARD_PCT,
    value_kind: 'percent',
    default_value: BALANCE_AWARD_PCT,
    editable: true,
    ledger_actions: ['dynamic_balance_award'],
  },
  {
    id: 'dynamic_mentorship',
    category: 'dynamic',
    title_zh: '辅导奖',
    description_zh: '平衡奖的代数比例（1圈40%，2-5圈10%…）由等级表决定',
    trigger_zh: '平衡奖入账后向上代发放',
    theory_ref: '§5.2',
    config_key: null,
    value_kind: 'json',
    default_value: JSON.stringify({
      note: '见 @uo/shared levels.ts mentorPctAtCircle；子号不产生辅导链',
      editable: false,
    }),
    editable: false,
    ledger_actions: ['mentorship_bonus'],
  },
  {
    id: 'dynamic_peer_level',
    category: 'dynamic',
    title_zh: '平级奖',
    description_zh: '下级晋级 A2/A3/A4 时上级固定 EP',
    trigger_zh: '晋级事件',
    theory_ref: '§5',
    config_key: 'dynamic.peer_bonus_ep',
    value_kind: 'amount',
    default_value: PEER_LEVEL_BONUS_EP,
    editable: true,
    ledger_actions: ['peer_level_bonus'],
  },
  {
    id: 'dynamic_share_bonus',
    category: 'dynamic',
    title_zh: '分享奖（级差）',
    description_zh: '激活主号时向上代分配 EP 池；累计 cap 按上级 level + 套餐',
    trigger_zh: 'POST activate-main 成功',
    theory_ref: '§5.3',
    config_key: 'reward.share_bonus.pools',
    value_kind: 'json',
    default_value: JSON.stringify(
      { E1: SHARE_BONUS_E1_CAPS, E2: SHARE_BONUS_E2_CAPS, E3_total: SHARE_BONUS_E3_TOTAL },
      null,
      2,
    ),
    editable: true,
    ledger_actions: ['share_bonus'],
  },
  {
    id: 'withdraw_split_static',
    category: 'withdraw_split',
    title_zh: '提现/UOC 拆分 · 静态轨',
    description_zh: 'UOC 确认拆分或旧提现 confirm-split 的静态比例',
    trigger_zh: 'Admin confirm-split（UOC 或 withdraw）',
    theory_ref: '§7.2',
    config_key: 'reward.withdraw.split_static',
    value_kind: 'json',
    default_value: JSON.stringify(WITHDRAW_SPLIT_STATIC, null, 2),
    editable: true,
    ledger_actions: ['withdraw_split_credit', 'withdraw_split_fee', 'withdraw_split_burn'],
  },
  {
    id: 'withdraw_split_dynamic',
    category: 'withdraw_split',
    title_zh: '提现/UOC 拆分 · 动态轨',
    description_zh: '动态轨 EP2/SP2/TP 手续费/FC 销毁',
    trigger_zh: 'Admin confirm-split',
    theory_ref: '§7.2',
    config_key: 'reward.withdraw.split_dynamic',
    value_kind: 'json',
    default_value: JSON.stringify(WITHDRAW_SPLIT_DYNAMIC, null, 2),
    editable: true,
    ledger_actions: ['withdraw_split_credit', 'withdraw_split_fee', 'withdraw_split_burn'],
  },
  {
    id: 'registration_main_sp2',
    category: 'activation',
    title_zh: '主号开通 · SP2',
    description_zh: '主号开通扣减 SP2 数量',
    trigger_zh: 'POST /api/users/activate-main',
    theory_ref: '§9',
    config_key: REGISTRATION_CONFIG_KEYS.MAIN_SP2,
    value_kind: 'amount',
    default_value: REGISTRATION_PACKAGE_DEFAULTS.sp2,
    editable: true,
  },
  {
    id: 'registration_main_tp',
    category: 'activation',
    title_zh: '主号开通 · TP',
    description_zh: '主号开通扣减 TP 数量',
    trigger_zh: 'POST /api/users/activate-main',
    theory_ref: '§9',
    config_key: REGISTRATION_CONFIG_KEYS.MAIN_TP,
    value_kind: 'amount',
    default_value: REGISTRATION_PACKAGE_DEFAULTS.tp,
    editable: true,
  },
  {
    id: 'registration_main_fc',
    category: 'activation',
    title_zh: '主号开通 · FC',
    description_zh: '主号开通扣减 FC 数量',
    trigger_zh: 'POST /api/users/activate-main',
    theory_ref: '§9',
    config_key: REGISTRATION_CONFIG_KEYS.MAIN_FC,
    value_kind: 'amount',
    default_value: REGISTRATION_PACKAGE_DEFAULTS.fc,
    editable: true,
  },
  {
    id: 'registration_auto_activate',
    category: 'activation',
    title_zh: '注册自动主号开通',
    description_zh: '注册后余额足够则自动 activate-main',
    trigger_zh: 'POST /api/auth/register',
    theory_ref: '§9',
    config_key: REGISTRATION_CONFIG_KEYS.AUTO_ACTIVATE_MAIN,
    value_kind: 'boolean',
    default_value: 'false',
    editable: true,
  },
  {
    id: 'rp_sp3_quota',
    category: 'quota',
    title_zh: 'RP→SP3 等级配额',
    description_zh: '每等级 RP 转 SP3 累计上限；键名 rp_sp3.quota.{level_code}',
    trigger_zh: 'POST /api/users/convert/rp-sp3',
    theory_ref: '§8.3',
    config_key: 'rp_sp3.quota.*',
    value_kind: 'json',
    default_value: JSON.stringify({ pattern: 'rp_sp3.quota.{level}', note: '见等级页或逐键编辑' }),
    editable: true,
  },
  {
    id: 'feature_dynamic_perf_feed',
    category: 'feature',
    title_zh: '自动业绩投喂',
    description_zh: '激活/子号/晋级/充值审核后向上投喂对碰业绩',
    trigger_zh: '业务事件',
    theory_ref: '§5.1',
    config_key: 'feature.dynamic_perf_feed',
    value_kind: 'boolean',
    default_value: 'true',
    editable: true,
  },
];

export const REWARD_DISTRIBUTION_BY_ID = Object.fromEntries(
  REWARD_DISTRIBUTION_REGISTRY.map((d) => [d.id, d]),
) as Record<string, RewardDistributionDef>;

export const REWARD_DIST_CATEGORY_LABELS: Record<RewardDistributionCategory, string> = {
  static: '静态奖',
  dynamic: '动态奖',
  withdraw_split: '提现/UOC 拆分',
  activation: '开通/注册',
  quota: '配额',
  feature: '开关',
};
